// Волна 37 (боты «золото+»; гейты подчастей T, F, B1 — у вызывающего кода:
// checkIfW36Feature(player, gameType, isPreW37…Player) из evaluation/goldPlus):
// угроза своему городу В ЭТОМ раунде и фронтирный город.
//
// Угроза этого раунда = модель волны 32 (отряд противника, который ходит в фазе
// войны после бота, встает на квартал; или враг уже на квартале — level
// THIS_ROUND) ИЛИ «фантомный найм»: ходящий после бота нанимает на своей
// свободной рабочей казарме самый быстрый доступный тип и доводит его до
// квартала в той же фазе (collectPhantomRecruits в cityCaptureThreat). Откуда:
// на 200 прод-реплеях (01.10–26.10) города золото+ с THIS_ROUND захвачены к концу
// раунда в 49.9% (95.6% всех захватов), только с фантомом — в 8.8% (еще 4.2%),
// NEXT_ROUND и без угрозы — 3 захвата из 14 352 город-ходов.
//
// Фронтир — город, который НЕ внутренний по логике гарнизонов волны 35
// (isInteriorCity из evaluation/garrisons): живой противник (отряд или квартал
// его города) не дальше W35_GARRISON_EXPOSURE_DISTANCE от кварталов, и не каждый
// такой источник прикрыт другим своим городом на кратчайшем пути (или кольцо
// клеток вокруг кварталов не целиком из клеток других своих городов). На прод-
// реплеях: 68% город-ходов золото+ и 1 430 из 1 434 захватов; внутренние — 10% и
// 4 захвата, безопасные (противника в 6 клетках нет) — 22% и 0.
import {
  selectCitiesCache,
  selectHexes,
  selectUnits,
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {W35_GARRISON_EXPOSURE_DISTANCE} from "../../../configs/botGarrison";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {getCityHexKeys} from "../../gameState/getCityHexKeys";
import {getHexDistance} from "../../coords/getHexDistance";
import {isHexTokenIsBlockerForMovement} from "../../movement/isHexTokenIsBlockerForMovement";
import {getBotGridRenderer, getNeighborHexKeys} from "../botGrid";
import {CITY_THREAT_LEVELS, getCityCaptureThreats} from "./cityCaptureThreat";
import {isInteriorCity} from "./garrisons";

/**
 * Угроза этого раунда по записи города getCityCaptureThreats(..., {withRecruits: true}):
 * отряды ходящих после бота (level THIS_ROUND) или фантомный найм
 * (isThisRoundByRecruit). Запись без withRecruits — только по level
 */
export const checkIfW37ThisRoundThreat = (cityThreat) => Boolean(cityThreat)
  && (cityThreat.level === CITY_THREAT_LEVELS.THIS_ROUND || cityThreat.isThisRoundByRecruit === true);

// Кэш на состояние: найм и перемещение спрашивают про каждый город на каждом
// тике, а состояние redux на тике одно (новое — после любого действия). Внутри
// getCityCaptureThreats досягаемость и фантомы и так кэшируются на позицию —
// здесь не пересобираются записи городов
let thisRoundMemo = null;

/**
 * Все записи городов бота с полями фантомного найма: результат
 * getCityCaptureThreats(state, botPlayer, {withRecruits: true, humanRecruitsOnly})
 * — {cityThreats, threatByQuarter}. Один объект на одно состояние: записи не мутировать
 */
export const getW37ThisRoundCityThreats = (state, botPlayer, {humanRecruitsOnly = false} = {}) => {
  if (thisRoundMemo && thisRoundMemo.state === state && thisRoundMemo.playerId === botPlayer.id
    && thisRoundMemo.humanRecruitsOnly === humanRecruitsOnly
  ) {
    return thisRoundMemo.result;
  }
  const result = getCityCaptureThreats(state, botPlayer, {withRecruits: true, humanRecruitsOnly});
  thisRoundMemo = {state, playerId: botPlayer.id, humanRecruitsOnly, result};
  return result;
};

const createNoThreat = () => ({
  isThreatened: false,
  byUnits: false,
  byRecruit: false,
  recruits: [],
  recruitDamage: 0,
  threatDamage: 0,
  isHolderAtRisk: false,
  cityThreat: null,
});

/**
 * Угрожает ли своему городу захват в этом раунде.
 * cityCentralQuarter — центральный квартал города (подойдет и любой его квартал).
 * options.humanRecruitsOnly — фантомы только живых противников (без isBot): на
 * прод-реплеях фантом бота сбывается в 4.5%, живого — в 36%.
 * Возвращает {
 *   isThreatened — byUnits || byRecruit;
 *   byUnits — level THIS_ROUND модели волны 32 (отряды ходящих после бота, враг на квартале);
 *   byRecruit — фантомный найм ходящего после бота встает на квартал города;
 *   recruits — такие фантомы ({enemyId, isEnemyBot, barracksHexKey, unitTypeId,
 *     speed, price, expectedSize, expectedDamage, stopCells});
 *   recruitDamage — наибольший ожидаемый урон фантома (держатель должен его пережить);
 *   threatDamage — ожидаемый урон отрядов по держателям (волна 32);
 *   isHolderAtRisk — держатели есть, но не переживут: isHolderAtRisk волны 32
 *     при byUnits или isRecruitHolderAtRisk фантома;
 *   cityThreat — запись города getCityCaptureThreats (null — не свой город)
 * }
 */
export const checkIfCityThreatenedThisRound = (state, botPlayer, cityCentralQuarter, {humanRecruitsOnly = false} = {}) => {
  const {threatByQuarter} = getW37ThisRoundCityThreats(state, botPlayer, {humanRecruitsOnly});
  const cityThreat = threatByQuarter.get(cityCentralQuarter) || null;
  if (!cityThreat) {
    return createNoThreat();
  }
  const byUnits = cityThreat.level === CITY_THREAT_LEVELS.THIS_ROUND;
  const byRecruit = cityThreat.isThisRoundByRecruit === true;
  return {
    isThreatened: byUnits || byRecruit,
    byUnits,
    byRecruit,
    recruits: cityThreat.recruits,
    recruitDamage: cityThreat.recruitDamage,
    threatDamage: cityThreat.threatDamage,
    isHolderAtRisk: (byUnits && cityThreat.isHolderAtRisk) || cityThreat.isRecruitHolderAtRisk === true,
    cityThreat,
  };
};

const minDistanceToKeys = (hexKey, keys) => {
  let best = Infinity;
  for (const key of keys) {
    const distance = getHexDistance(hexKey, key);
    if (distance < best) {
      best = distance;
    }
  }
  return best;
};

/**
 * Чистое ядро фронтира (без стора, для тестов): Set центральных кварталов
 * фронтирных городов из ownCities. Аргументы — как у isInteriorCity (garrisons):
 * enemyQuarterKeys — кварталы городов живых противников; enemyUnits — отряды
 * живых противников (без нейтралов); ownCityCellsByCityKey — Map центральный
 * квартал → клетки города; isPassable(hexKey); getNeighbors(hexKey).
 * Город без противника в W35_GARRISON_EXPOSURE_DISTANCE — безопасный, не фронтир
 * (у гарнизонов isInteriorCity зовется только при враге в этой дистанции)
 */
export const collectW37FrontierCityKeys = ({
  ownCities, ownCityCellsByCityKey, enemyQuarterKeys, enemyUnits, isPassable, getNeighbors,
}) => {
  const frontierKeys = new Set();
  const sourceKeys = [...enemyQuarterKeys, ...enemyUnits.map((unit) => unit.hexKey)];
  (ownCities || []).forEach((city) => {
    const quarters = getAllQuartersOfCity(city);
    const isNear = sourceKeys.some((sourceKey) => minDistanceToKeys(sourceKey, quarters) <= W35_GARRISON_EXPOSURE_DISTANCE);
    if (isNear && !isInteriorCity({
      cityKey: city.centralQuarter, quarters, ownCities, ownCityCellsByCityKey, enemyQuarterKeys, enemyUnits,
      isPassable, getNeighbors,
    })) {
      frontierKeys.add(city.centralQuarter);
    }
  });
  return frontierKeys;
};

// Кэш фронтира на позицию: зависит от своих и чужих городов (кэш городов),
// клеток отрядов живых противников, клеток поля и сетки
let frontierMemo = null;

/** Set центральных кварталов фронтирных городов игрока (см. checkIfW37FrontierCity). Не мутировать */
export const getW37FrontierCityKeys = (state, player) => {
  const citiesCache = selectCitiesCache(state);
  const ownCities = (player && citiesCache?.[player.color]) || [];
  if (ownCities.length === 0) {
    return new Set();
  }
  const hexes = selectHexes(state);
  const gridRenderer = getBotGridRenderer(state);
  const enemyUnits = (selectUnits(state) || [])
    .filter((unit) => unit.ownerId !== player.id && unit.ownerId !== NEUTRAL_PLAYER_ID);
  const key = `${player.id}#${player.color}#${enemyUnits.map((unit) => unit.hexKey).join('|')}`;
  if (frontierMemo && frontierMemo.key === key && frontierMemo.citiesCache === citiesCache
    && frontierMemo.hexes === hexes && frontierMemo.gridRenderer === gridRenderer
  ) {
    return frontierMemo.result;
  }
  const enemyQuarterKeys = Object.entries(citiesCache || {})
    .filter(([color]) => color !== player.color && color !== NEUTRAL_PLAYER_COLOR)
    .flatMap(([, cities]) => (cities || []).flatMap((city) => getAllQuartersOfCity(city)));
  const result = collectW37FrontierCityKeys({
    ownCities,
    ownCityCellsByCityKey: new Map(ownCities.map((city) => [city.centralQuarter, getCityHexKeys(city)])),
    enemyQuarterKeys,
    enemyUnits,
    isPassable: (hexKey) => Boolean(gridRenderer.hexes[hexKey]) && !isHexTokenIsBlockerForMovement(hexes[hexKey]),
    getNeighbors: (hexKey) => getNeighborHexKeys(hexKey, gridRenderer),
  });
  frontierMemo = {key, citiesCache, hexes, gridRenderer, result};
  return result;
};

/**
 * Фронтирный ли свой город (п. 1 запроса владельца: «не окруженный со всех
 * сторон своими городами»): НЕ внутренний по isInteriorCity и с живым
 * противником (отряд или квартал) не дальше W35_GARRISON_EXPOSURE_DISTANCE (6)
 * от кварталов. cityCentralQuarter — центральный квартал своего города; state;
 * player — игрок-владелец (бот). Чужой или несуществующий город — false.
 * Чисто геометрия: угроза, очередь хода и последний раунд не учитываются
 */
export const checkIfW37FrontierCity = (cityCentralQuarter, state, player) => (
  getW37FrontierCityKeys(state, player).has(cityCentralQuarter)
);
